import { Target, Users, Bike, Star, TrendingUp, Clock } from 'lucide-react';

export function PerformanceMetrics({ avgOrders, waiterPerformance, riderPerformance }) {
  console.log('waiterPerformance:', waiterPerformance);
  console.log('riderPerformance:', riderPerformance);

  // Ensure all props are arrays
  const safeAvgOrders = Array.isArray(avgOrders) ? avgOrders : [];
  const safeWaiterPerformance = Array.isArray(waiterPerformance) ? waiterPerformance : [];
  const safeRiderPerformance = Array.isArray(riderPerformance) ? riderPerformance : [];

  // Filter out zero-performance items
  const activeWaiters = safeWaiterPerformance.filter(w => w.orders > 0);
  const activeRiders = safeRiderPerformance.filter(r => r.orders > 0);

  return (
    <>
      {/* Average Orders by Type */}
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200 hover:shadow-xl transition-all">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-bold text-slate-900">Average Order Value</h3>
            <p className="text-sm text-slate-500 mt-1">Performance by order type</p>
          </div>
          <div className="bg-gradient-to-br from-cyan-500 to-cyan-600 p-3 rounded-xl shadow-lg">
            <Target className="w-5 h-5 text-white" />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {safeAvgOrders.length === 0 ? (
            <div className="col-span-3 text-center py-8">
              <Target className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-400">No average order data available</p>
            </div>
          ) : (
            safeAvgOrders.map((item, index) => {
              // Ensure item has the expected properties
              const type = item.type || 'Order';
              const avg = item.avg || 0;
              const target = item.target || avg * 1.2;
              const achievement = item.achievement || ((avg / (target || 1)) * 100) || 0;
              const cappedAchievement = Math.min(achievement, 100);
              
              return (
                <div key={index} className="p-4 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border border-slate-200 hover:shadow-md transition-all">
                  <p className="text-xs text-slate-600 font-medium mb-2">{type}</p>
                  <p className="text-xl font-bold text-slate-900 mb-2">
                    Rs {avg.toLocaleString(undefined, { 
                      minimumFractionDigits: 2, 
                      maximumFractionDigits: 2 
                    })}
                  </p>
                  
                  {/* Progress Bar */}
                  <div className="relative w-full h-2 bg-slate-200 rounded-full overflow-hidden mb-2">
                    <div 
                      className={`absolute top-0 left-0 h-full rounded-full transition-all ${
                        cappedAchievement >= 100 ? 'bg-gradient-to-r from-green-500 to-green-600' : 'bg-gradient-to-r from-[#01796d] to-teal-600'
                      }`}
                      style={{ width: `${cappedAchievement}%` }}
                    ></div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500">
                      Target: Rs {target.toLocaleString()}
                    </span>
                    <span className={`text-xs font-bold ${cappedAchievement >= 100 ? 'text-green-600' : 'text-[#01796d]'}`}>
                      {cappedAchievement.toFixed(1)}%
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Staff Performance Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Waiter Performance */}
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200 hover:shadow-xl transition-all">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-bold text-slate-900">Waiter Performance</h3>
              <p className="text-sm text-slate-500 mt-1">Top performing staff</p>
            </div>
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-3 rounded-xl shadow-lg">
              <Users className="w-5 h-5 text-white" />
            </div>
          </div>

          <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
            {activeWaiters.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-400">No waiter data available</p>
              </div>
            ) : (
              activeWaiters.sort((a, b) => b.sales - a.sales).map((waiter, index) => (
                <div key={index} className="p-4 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border border-slate-200 hover:shadow-md transition-all">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                        <span className="text-white font-bold text-sm">
                          {waiter.name ? waiter.name.charAt(0).toUpperCase() : '?'}
                        </span>
                      </div>
                      <div>
                        <p className="font-bold text-slate-900">{waiter.name || 'Unknown'}</p>
                        <div className="flex items-center gap-1">
                          <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                          <span className="text-xs text-slate-600">{waiter.rating || '3.5'}</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-[#01796d]">
                        Rs {(waiter.sales || 0).toLocaleString()}
                      </p>
                      <p className="text-xs text-slate-500">{waiter.orders || 0} orders</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-xs text-slate-600 mt-2 pt-2 border-t border-slate-200">
                    <span>Avg Order:</span>
                    <span className="font-semibold">
                      Rs {(waiter.avgOrder || 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Rider Performance */}
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200 hover:shadow-xl transition-all">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-bold text-slate-900">Rider Performance</h3>
              <p className="text-sm text-slate-500 mt-1">Delivery efficiency</p>
            </div>
            <div className="bg-gradient-to-br from-green-500 to-green-600 p-3 rounded-xl shadow-lg">
              <Bike className="w-5 h-5 text-white" />
            </div>
          </div>

          <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
            {activeRiders.length === 0 ? (
              <div className="text-center py-8">
                <Bike className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-400">No rider data available</p>
              </div>
            ) : (
              activeRiders.sort((a, b) => b.sales - a.sales).map((rider, index) => (
                <div key={index} className="p-4 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border border-slate-200 hover:shadow-md transition-all">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center">
                        <Bike className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-bold text-slate-900">{rider.name || 'Unknown'}</p>
                        <p className="text-xs text-slate-500">{rider.orders || 0} deliveries</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-[#01796d]">
                        Rs {(rider.sales || 0).toLocaleString()}
                      </p>
                      <div className="flex items-center gap-1 justify-end">
                        <Clock className="w-3 h-3 text-green-600" />
                        <span className="text-xs text-green-600 font-semibold">{rider.onTime || 95}% on-time</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-xs text-slate-600 mt-2 pt-2 border-t border-slate-200">
                    <span>Avg Order:</span>
                    <span className="font-semibold">
                      Rs {(rider.avgOrder || 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Add custom scrollbar styles */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>
    </>
  );
}